import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { listSends } from "utils/collegeDocumentService";
import { SENDS_QUERY_KEY } from "./documentLibraryQueryKeys";
import { libraryErrorMessage } from "./libraryErrorMessage";
import { buildSendsQueryParams } from "./sendsQueryParams";
import { STATUS_COLOURS, STATUS_FILTER_OPTIONS } from "./sendStatusMeta";
import { formatDisplayDateTime } from "utils/dateFormat";

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];
const DEFAULT_PAGE_SIZE = 20;

const SentDocumentsTab = ({ universityId = null }) => {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [appliedQ, setAppliedQ] = useState("");

  const sendsQuery = useQuery({
    queryKey: [SENDS_QUERY_KEY, universityId, page, size, status, appliedQ],
    queryFn: () =>
      listSends(buildSendsQueryParams({ universityId, status, q: appliedQ, page, size })),
    keepPreviousData: true,
  });

  const rows = sendsQuery.data?.content || [];
  const total = sendsQuery.data?.totalItems ?? 0;
  const columnCount = universityId ? 8 : 9;

  const runSearch = () => {
    setPage(0);
    setAppliedQ(q);
  };

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          label="Search client, email, passport or document"
          value={q}
          onChange={(event) => setQ(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") runSearch();
          }}
        />
        <TextField
          select
          size="small"
          label="Status"
          sx={{ minWidth: 220 }}
          value={status}
          onChange={(event) => {
            setPage(0);
            setStatus(event.target.value);
          }}
        >
          {STATUS_FILTER_OPTIONS.map((option) => (
            <MenuItem key={option.value || "any"} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {sendsQuery.isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {sendsQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {libraryErrorMessage(sendsQuery.error, "Could not load sent documents")}
        </Alert>
      )}

      {!sendsQuery.isLoading && !sendsQuery.isError && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                {!universityId && <TableCell>College</TableCell>}
                <TableCell>Document</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Admission</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sent</TableCell>
                <TableCell>Signed</TableCell>
                <TableCell>Sent by</TableCell>
                <TableCell align="right">Signed copy</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.packetId} hover>
                  {!universityId && <TableCell>{row.collegeName || "-"}</TableCell>}
                  <TableCell>{row.documentTitle}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.clientName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.clientEmail}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.admissionId ? (
                      <a href={`/admin/admission/edit/${row.admissionId}`}>#{row.admissionId}</a>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={STATUS_COLOURS[row.status] || "default"}
                      label={row.status}
                    />
                  </TableCell>
                  <TableCell>{formatDisplayDateTime(row.sentAt)}</TableCell>
                  <TableCell>{formatDisplayDateTime(row.signedAt)}</TableCell>
                  <TableCell>{row.sentBy || "-"}</TableCell>
                  <TableCell align="right">
                    {row.signedPdfUrl ? (
                      <a href={row.signedPdfUrl} target="_blank" rel="noreferrer">
                        Download
                      </a>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columnCount}>
                    <Typography variant="body2" color="text.secondary">
                      Nothing sent yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={size}
            onPageChange={(_, next) => setPage(next)}
            onRowsPerPageChange={(event) => {
              setSize(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        </>
      )}
    </Box>
  );
};

export default SentDocumentsTab;
